import axios from 'axios';

// Define token names
const ADMIN_TOKEN = 'pms_admin_';
const EMPLOYEE_TOKEN = 'pms_employee_';

// Track if a redirect is already in progress
let isRedirecting = false;

// Function to clear all auth tokens/data
const clearAuthData = () => {
  // Clear cookies
  document.cookie = `${ADMIN_TOKEN}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  document.cookie = `${EMPLOYEE_TOKEN}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  
  // Clear localStorage
  localStorage.removeItem('auth');
  localStorage.removeItem('users-data');
  localStorage.removeItem('rememberedCredentials');
};

const axiosInstance = axios.create({
  baseURL: '/api', // Restore '/api' prefix as API routes need it
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to handle auth headers
axiosInstance.interceptors.request.use(
  config => {
    // Check for admin token first
    let token = document.cookie
      .split('; ')
      .find(row => row.startsWith(`${ADMIN_TOKEN}=`))
      ?.split('=')[1];

    // If no admin token found, check for employee token
    if (!token) {
      token = document.cookie
        .split('; ')
        .find(row => row.startsWith(`${EMPLOYEE_TOKEN}=`))
        ?.split('=')[1];
    }

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
      
      // Debug log the token type for debugging auth issues (hide actual token value)
      const tokenType = document.cookie.includes(`${ADMIN_TOKEN}=`) ? 'admin' : 'employee';
      console.log(`[Axios] Using ${tokenType} auth token for request to: ${config.url}`);
    } else {
      console.warn(`[Axios] No auth token found for request to: ${config.url}`);
    }
    
    return config;
  },
  error => Promise.reject(error)
);

// Add response interceptor to handle auth errors
axiosInstance.interceptors.response.use(
  response => response,
  error => {
    // Check if we're already redirecting to avoid duplicate redirects
    if (isRedirecting) {
      return Promise.reject(error);
    }

    const isLoginPage = window.location.pathname.includes('/auth/login');

    if (error.response && error.response.status === 401) {
      // Check if this is a logout request
      const isLogoutRequest = error.config.url && error.config.url.includes('/signout');

      if (isLogoutRequest) {
        // For logout requests, we still want to complete the logout flow even if there's a 401
        console.log('401 during logout - continuing logout flow');
        
        // Clear all auth data
        clearAuthData();

        // Only redirect if not already on login page
        if (!isLoginPage) {
          // Prevent duplicate redirects
          isRedirecting = true;
          
          // Use a timeout to prevent immediate redirect during logout flow
          setTimeout(() => {
            window.location.href = '/auth/login';
            // Reset the redirect flag after some time
            setTimeout(() => {
              isRedirecting = false;
            }, 1000);
          }, 100);
        }

        // Return a resolved promise for logout requests to prevent error handling
        return Promise.resolve({ data: { success: true } });
      }

      // For other 401 errors, clear auth data and redirect
      clearAuthData();

      // For other 401 errors, redirect if not on login page
      if (!isLoginPage) {
        // Prevent duplicate redirects
        isRedirecting = true;
        
        // Redirect with a small delay
        setTimeout(() => {
          window.location.href = '/auth/login';
          // Reset the redirect flag after some time
          setTimeout(() => {
            isRedirecting = false;
          }, 1000);
        }, 100);
      }
    }
    return Promise.reject(error);
  }
);

// Get projects by status
export const getProjectsByStatus = async (status: string, limit: number = 5) => {
  try {
    const response = await axiosInstance.get(
      `/v1/admin/projects/status/${status}?limit=${limit}`
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching projects by status (${status}):`, error);
    throw error;
  }
};

export default axiosInstance;
